import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle2, CreditCard, Loader2, Lock, MapPin, Pencil, Plus, ShoppingBag, Store, Trash2, Truck, UserRound } from 'lucide-react';
import { ShopChrome, SignInPrompt } from '@/components/shop/ShopChrome';
import PriceSummary from '@/components/shop/PriceSummary';
import AddressForm from '@/components/shop/AddressForm';
import { Img, Shimmer } from '@/components/ui';
import { deleteAddress, getAddresses, placeShopOrder, previewCheckout } from '@/api/shop';
import { payShopOrder } from '@/lib/shopPayment';
import { useAuth } from '@/context/AuthContext';
import { useRazorpay } from '@/services/paymentService';
import { refreshCartCount } from '@/lib/shopStore';
import { friendlyError } from '@/lib/apiError';
import { newIdempotencyKey, rupees } from '@/lib/shop';

// Errors that mean "what you are buying changed": go back and look again.
const STALE_CODES = new Set(['CART_CHANGED', 'OUT_OF_STOCK', 'NOT_AVAILABLE', 'EMPTY_CART']);

const Step = ({ n, title, done, children, action }) => (
  <section className="lk-card overflow-hidden">
    <header className="flex items-center justify-between gap-3 border-b border-ink-200/70 px-5 py-3.5">
      <h2 className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-ink-900">
        <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${done ? 'bg-success text-white' : 'bg-brand-primary text-white'}`}>
          {done ? <CheckCircle2 className="h-4 w-4" /> : n}
        </span>
        {title}
      </h2>
      {action}
    </header>
    <div className="p-5">{children}</div>
  </section>
);

const formatAddress = (a) => [a.addressLine || a.line, a.landmark, a.city, a.state].filter(Boolean).join(', ');

/**
 * The receiver's details. AuthContext fills a missing name with "+91-<phone>"
 * — that is not a name, so it never pre-fills (or gets sent as) one.
 */
const realName = (user) => {
  const n = String(user?.name || '').trim();
  return !n || /^\+?91-?\d*$/.test(n) || /^\d+$/.test(n) ? '' : n;
};
const tenDigits = (v) => String(v || '').replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '').slice(-10);
const PHONE_RE = /^[6-9]\d{9}$/;
const contactErrors = ({ name, phone }) => ({
  ...(name.trim().length < 2 ? { name: 'Enter the name of the person receiving the order' } : {}),
  ...(!PHONE_RE.test(phone) ? { phone: 'Enter a 10-digit mobile number' } : {}),
});

const CheckoutPage = () => {
  const { user, openAuthModal } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { initiatePayment } = useRazorpay();

  // `?buy=<listingId>:<qty>` is Buy now (one item, cart untouched); otherwise the cart.
  // `from` is the product's slug, so a stale Buy now goes back to that product.
  const buy = params.get('buy');
  const from = params.get('from');
  const backTo = from ? `/shop/p/${encodeURIComponent(from)}` : '/shop';
  const input = useMemo(() => {
    if (!buy) return { fromCart: true };
    const [listingId, qty] = buy.split(':').map(Number);
    return { items: [{ listingId, qty: qty || 1 }] };
  }, [buy]);

  const [preview, setPreview] = useState(null);
  const [addresses, setAddresses] = useState(null);
  const [addressId, setAddressId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // address being edited
  const [confirmDelete, setConfirmDelete] = useState(null); // address id
  const [deletingId, setDeletingId] = useState(null);
  const [contact, setContact] = useState(() => ({ name: realName(user), phone: tenDigits(user?.mobile || user?.phone) }));
  const [contactTouched, setContactTouched] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);

  // The user can arrive (login) after the first render: fill blanks then.
  useEffect(() => {
    if (!user) return;
    setContact((c) => ({ name: c.name || realName(user), phone: c.phone || tenDigits(user.mobile || user.phone) }));
  }, [user]);
  // One key per checkout attempt: a retry after a network blip or a closed
  // payment window returns the same order instead of creating a second one.
  const keyRef = useRef(null);

  const bounce = useCallback(
    (message) => {
      if (input.fromCart) navigate('/shop/cart', { replace: true, state: { notice: message } });
      else {
        // Buy now: back to the product (or the shop), never history -1 —
        // that may be another site or the login page.
        toast.error(message);
        navigate(backTo, { replace: true });
      }
    },
    [input, navigate, backTo]
  );

  useEffect(() => {
    if (!user) return;
    let live = true;
    previewCheckout(input)
      .then((p) => {
        if (!live) return;
        if (!p?.groups?.length) navigate('/shop/cart', { replace: true });
        else setPreview(p);
      })
      .catch((err) => {
        if (!live) return;
        const code = err?.response?.data?.code;
        if (code === 'EMPTY_CART') navigate('/shop/cart', { replace: true });
        else if (STALE_CODES.has(code) || err?.response?.status === 409) bounce(friendlyError(err));
        else setError(friendlyError(err, 'Checkout could not be loaded'));
      });
    getAddresses()
      .then((list) => {
        if (!live) return;
        const active = (list || []).filter((a) => a.status !== 'inactive');
        setAddresses(active);
        const def = active.find((a) => a.isDefault) || active[0];
        if (def) setAddressId(def.id);
        else setAdding(true);
      })
      .catch(() => live && setAddresses([]));
    return () => {
      live = false;
    };
  }, [user, input, bounce, navigate]);

  // A different address or receiver is a different order: new key.
  useEffect(() => {
    keyRef.current = null;
  }, [addressId, contact.name, contact.phone]);

  const reloadAddresses = async (preferId) => {
    const list = (await getAddresses()).filter((a) => a.status !== 'inactive');
    setAddresses(list);
    const keep = list.find((a) => a.id === preferId) || list.find((a) => a.isDefault) || list[0];
    setAddressId(keep?.id ?? null);
    if (!list.length) setAdding(true);
    return list;
  };

  const removeAddress = async (a) => {
    setDeletingId(a.id);
    try {
      await deleteAddress(a.id);
      toast.success('Address deleted');
      setConfirmDelete(null);
      await reloadAddresses(a.id === addressId ? null : addressId);
    } catch (err) {
      toast.error(friendlyError(err, 'Address could not be deleted'));
    } finally {
      setDeletingId(null);
    }
  };

  const cErrors = contactErrors(contact);
  const contactOk = Object.keys(cErrors).length === 0;

  const finish = async (orderId) => {
    refreshCartCount();
    navigate(`/shop/orders/${orderId}?placed=1`, { replace: true });
  };

  const pay = async () => {
    if (!addressId) {
      toast.error('Pick a delivery address');
      return;
    }
    if (!contactOk) {
      setContactTouched(true);
      toast.error(cErrors.name || cErrors.phone);
      return;
    }
    setPaying(true);
    if (!keyRef.current) keyRef.current = newIdempotencyKey('shop');
    let out;
    try {
      out = await placeShopOrder({
        idempotencyKey: keyRef.current,
        input: { ...input, addressId, contact: { name: contact.name.trim(), phone: contact.phone } },
      });
    } catch (err) {
      setPaying(false);
      const code = err?.response?.data?.code;
      if (STALE_CODES.has(code)) bounce(friendlyError(err));
      else {
        // OWN_LISTING, BAD_CONTACT, BAD_ADDRESS…: the server's message says what to fix.
        if (code === 'BAD_CONTACT') setContactTouched(true);
        toast.error(friendlyError(err, 'Your order could not be placed'));
      }
      return;
    }

    const { order, checkout } = out || {};
    if (!order || !checkout) {
      setPaying(false);
      toast.error('Your order could not be placed');
      return;
    }

    payShopOrder({
      order,
      checkout,
      initiatePayment,
      user: { ...user, name: contact.name.trim(), phone: contact.phone },
      onPaid: finish,
      onVerifyError: (err, orderId) => {
        setPaying(false);
        toast.error(friendlyError(err, 'We could not confirm the payment. If money was taken it will be confirmed shortly.'));
        navigate(`/shop/orders/${orderId}`, { replace: true });
      },
      onError: (msg) => {
        setPaying(false);
        toast.error(msg);
      },
      onDismiss: () => {
        setPaying(false);
        toast('Payment cancelled. Your items are held for a few minutes.', { icon: '⏳' });
      },
    });
  };

  if (!user) {
    return (
      <ShopChrome>
        <SignInPrompt icon={ShoppingBag} title="Log in to check out" message="We'll bring your cart along." onLogin={openAuthModal} />
      </ShopChrome>
    );
  }

  const selectedAddress = addresses?.find((a) => a.id === addressId);
  const groups = preview?.groups || [];
  const summary = preview?.summary;

  return (
    <ShopChrome hideBar>
      <div className="border-b border-ink-200/70 bg-white">
        <div className="lk-container flex items-center justify-between py-3">
          <h1 className="text-h3 font-semibold text-ink-900">Checkout</h1>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-success">
            <Lock className="h-3.5 w-3.5" /> Secure checkout
          </span>
        </div>
      </div>

      <div className="lk-container py-5 sm:py-8">
        {error ? (
          <div className="lk-card p-8 text-center">
            <p className="text-ink-700">{error}</p>
            <Link to={input.fromCart ? '/shop/cart' : backTo} className="lk-btn-primary mt-4">
              {input.fromCart ? 'Back to cart' : from ? 'Back to the product' : 'Back to the shop'}
            </Link>
          </div>
        ) : (
          <div className="grid items-start gap-6 lg:grid-cols-[1fr_360px]">
            <div className="min-w-0 space-y-4">
              {/* 1. Address */}
              <Step
                n={1}
                title="Delivery address"
                done={Boolean(selectedAddress) && !adding && !editing && contactOk}
                action={
                  addresses?.length > 0 && !adding && !editing ? (
                    <button type="button" onClick={() => setAdding(true)} className="lk-focus inline-flex items-center gap-1 rounded text-sm font-semibold text-brand-secondary">
                      <Plus className="h-4 w-4" /> Add new
                    </button>
                  ) : null
                }
              >
                {addresses === null ? (
                  <Shimmer className="h-20 w-full" />
                ) : adding ? (
                  <AddressForm
                    onCancel={addresses.length ? () => setAdding(false) : null}
                    onSaved={(a) => {
                      setAdding(false);
                      reloadAddresses(a?.id);
                    }}
                  />
                ) : editing ? (
                  <AddressForm
                    key={editing.id}
                    address={editing}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      const id = editing.id;
                      setEditing(null);
                      reloadAddresses(id);
                    }}
                  />
                ) : (
                  <div className="space-y-2" role="radiogroup" aria-label="Delivery address">
                    {addresses.map((a) => {
                      const on = a.id === addressId;
                      const asking = confirmDelete === a.id;
                      return (
                        <div
                          key={a.id}
                          className={`overflow-hidden rounded-2xl border transition ${on ? 'border-brand-secondary bg-brand-tint/50' : 'border-ink-200 hover:border-ink-400'}`}
                        >
                          <button
                            type="button"
                            role="radio"
                            aria-checked={on}
                            onClick={() => setAddressId(a.id)}
                            className="lk-focus flex w-full items-start gap-3 p-4 text-left"
                          >
                            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${on ? 'border-brand-secondary' : 'border-ink-400'}`}>
                              {on && <span className="h-2.5 w-2.5 rounded-full bg-brand-secondary" />}
                            </span>
                            <span className="min-w-0 flex-1 text-sm">
                              <span className="flex items-center gap-2 font-semibold text-ink-900">
                                {a.city || 'Address'}
                                {a.isDefault ? <span className="rounded bg-surface-sunken px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink-500">Default</span> : null}
                              </span>
                              <span className="mt-0.5 block text-ink-700">{formatAddress(a)}</span>
                              <span className="tabular block font-semibold text-ink-900">PIN {a.postalCode}</span>
                            </span>
                          </button>
                          <div className="flex flex-wrap items-center justify-end gap-1 border-t border-ink-200/70 px-2 py-1">
                            {asking ? (
                              <>
                                <span className="mr-auto pl-2 text-xs font-medium text-ink-700">Delete this address?</span>
                                <button type="button" onClick={() => setConfirmDelete(null)} disabled={deletingId === a.id} className="lk-focus min-h-[36px] rounded-lg px-3 text-xs font-semibold text-ink-700 hover:bg-surface-muted">
                                  Keep
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeAddress(a)}
                                  disabled={deletingId === a.id}
                                  className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-danger hover:bg-danger-tint"
                                >
                                  {deletingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                                  Delete
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setEditing(a)}
                                  aria-label={`Edit address ${formatAddress(a)}`}
                                  className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-ink-700 hover:bg-surface-muted"
                                >
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmDelete(a.id)}
                                  aria-label={`Delete address ${formatAddress(a)}`}
                                  className="lk-focus inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-ink-700 hover:bg-danger-tint hover:text-danger"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Receiver */}
                <fieldset className="mt-5 border-t border-ink-200/70 pt-4">
                  <legend className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                    <UserRound className="h-4 w-4 text-ink-500" /> Receiver
                  </legend>
                  <p className="mt-1 text-xs text-ink-500">The courier calls this number for delivery.</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block">
                      <span className="lk-label">Full name</span>
                      <input
                        value={contact.name}
                        onChange={(e) => setContact((c) => ({ ...c, name: e.target.value.slice(0, 80) }))}
                        onBlur={() => setContactTouched(true)}
                        autoComplete="name"
                        placeholder="Who will receive it"
                        aria-invalid={contactTouched && Boolean(cErrors.name)}
                        className={`lk-input mt-1 ${contactTouched && cErrors.name ? 'border-danger' : ''}`}
                      />
                      {contactTouched && cErrors.name && <span className="mt-1 block text-xs text-danger">{cErrors.name}</span>}
                    </label>
                    <label className="block">
                      <span className="lk-label">Mobile number</span>
                      <span className="relative mt-1 block">
                        <span className="tabular pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ink-500">+91</span>
                        <input
                          value={contact.phone}
                          onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                          onBlur={() => setContactTouched(true)}
                          inputMode="numeric"
                          autoComplete="tel-national"
                          maxLength={10}
                          placeholder="10-digit mobile"
                          aria-invalid={contactTouched && Boolean(cErrors.phone)}
                          className={`lk-input tabular pl-12 ${contactTouched && cErrors.phone ? 'border-danger' : ''}`}
                        />
                      </span>
                      {contactTouched && cErrors.phone && <span className="mt-1 block text-xs text-danger">{cErrors.phone}</span>}
                    </label>
                  </div>
                </fieldset>
              </Step>

              {/* 2. Items per seller */}
              <Step n={2} title="Order summary" done={Boolean(preview)}>
                {!preview ? (
                  <Shimmer className="h-32 w-full" />
                ) : (
                  <div className="space-y-4">
                    {groups.map((g, gi) => (
                      <div key={g.sellerId} className="overflow-hidden rounded-2xl border border-ink-200/70">
                        <div className="flex flex-wrap items-center justify-between gap-2 bg-surface-muted/70 px-4 py-2.5 text-xs">
                          <span className="flex items-center gap-1.5 font-semibold text-ink-900">
                            <Store className="h-3.5 w-3.5 text-ink-500" />
                            Shipment {gi + 1} of {groups.length} · {g.sellerName}
                          </span>
                          <span className="tabular text-ink-500">
                            Delivery {g.deliveryFee ? rupees(g.deliveryFee) : <span className="font-semibold text-success">Free</span>}
                          </span>
                        </div>
                        <ul className="divide-y divide-ink-200/70">
                          {g.items.map((i) => (
                            <li key={`${i.listingId}`} className="flex gap-3 p-4">
                              <Img src={i.image} alt={i.title} className="h-20 w-16 shrink-0 rounded-lg" />
                              <div className="min-w-0 flex-1 text-sm">
                                <p className="line-clamp-2 text-ink-900">{i.title}</p>
                                <p className="text-xs text-ink-500">
                                  {i.variantLabel} · Qty {i.qty}
                                </p>
                                <p className="tabular mt-1">
                                  <span className="font-bold text-ink-900">{rupees(i.lineTotal)}</span>
                                  {i.mrp > i.price && <span className="ml-2 text-xs text-ink-400 line-through">{rupees(i.lineMrp)}</span>}
                                </p>
                                {i.delivery?.label && (
                                  <p className="mt-1 inline-flex items-center gap-1 text-xs text-ink-500">
                                    <Truck className="h-3.5 w-3.5" /> Delivery by <span className="font-semibold text-ink-700">{i.delivery.label}</span>
                                  </p>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </Step>

              {/* 3. Pay */}
              <Step n={3} title="Payment">
                <div className="flex items-start gap-3 text-sm text-ink-700">
                  <CreditCard className="mt-0.5 h-5 w-5 shrink-0 text-ink-500" />
                  <p>
                    Pay securely with UPI, cards, netbanking or wallets. Cash on delivery is not available yet.
                    {import.meta.env.DEV && <span className="mt-1 block text-xs text-warning">Local test mode: the test gateway completes payment without a popup.</span>}
                  </p>
                </div>
                <button type="button" onClick={pay} disabled={!preview || !selectedAddress || adding || Boolean(editing) || paying} className="lk-btn-primary mt-4 hidden w-full sm:flex">
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {paying ? 'Processing…' : `Pay ${summary ? rupees(summary.total) : ''}`}
                </button>
              </Step>
            </div>

            <aside className="lg:sticky lg:top-24">
              {summary ? <PriceSummary summary={summary} groups={groups} /> : <Shimmer className="h-72 w-full rounded-2xl" />}
              {selectedAddress && (
                <p className="mt-3 flex items-start gap-2 px-1 text-xs text-ink-500">
                  <MapPin className="mt-px h-3.5 w-3.5 shrink-0" />
                  Delivering to {formatAddress(selectedAddress)} – {selectedAddress.postalCode}
                </p>
              )}
            </aside>
          </div>
        )}
      </div>

      {/* Phones: pay bar */}
      <div className="h-20 sm:hidden" />
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ink-200 bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 shadow-bar sm:hidden">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[11px] text-ink-500">Total</p>
            <p className="tabular text-lg font-bold leading-tight text-ink-900">{summary ? rupees(summary.total) : '—'}</p>
          </div>
          <button type="button" onClick={pay} disabled={!preview || !selectedAddress || adding || Boolean(editing) || paying} className="lk-btn-primary px-8">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {paying ? 'Processing…' : 'Pay now'}
          </button>
        </div>
      </div>
    </ShopChrome>
  );
};

export default CheckoutPage;
